/**
 * Slow, draggable ingredient marquee — one row on desktop, two counter-rotating rows on
 * mobile (Figma 3150:36535 and 3191:4283).
 *
 * The card markup is written once in Liquid. Two rows animating in opposite directions need
 * two separate lanes, so rather than duplicating that markup for mobile this element splits
 * the single source list into as many tracks as the breakpoint calls for and rebuilds when
 * the breakpoint changes.
 *
 * Motion is a `transform` on the lane, not `scrollLeft`. Driving scroll read well on desktop
 * but broke on real phones two ways: iOS owns the scroll offset on the compositor and
 * quantises it, so a per-frame write juddered instead of drifting; and a track whose clones
 * had not yet filled it (images still loading) was not scrollable at all, so every write was
 * a no-op and that row stood still while its neighbour ran — which row lost depended on load
 * timing, hence "the top one on some phones, the bottom one on others". A transform moves
 * regardless of scrollability and is composited, so it is smooth and deterministic.
 *
 * Dragging is handled directly on the same transform (pointer events, all input types), with
 * `touch-action: pan-y` leaving vertical page scrolling to the browser.
 *
 * Each track clones its row until it covers the track twice, then wraps by exactly one
 * row-plus-gap, so the loop has no visible join.
 */

const MOBILE_QUERY = '(max-width: 749px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const MAX_CLONES = 10;
const RESUME_DELAY = 1200;
/** Movement in px before a press counts as a drag rather than a tap. */
const DRAG_THRESHOLD = 4;

/** One lane that drifts sideways and can be dragged. */
class MarqueeTrack {
  #element;
  #lane;
  #row;
  #seconds;
  #direction;
  #pauseOnHover;
  #speedScale = 1;

  #loopWidth = 0;
  #position = 0;
  #pixelsPerMs = 0;

  #hovering = false;
  #dragging = false;
  #pointerId = null;
  #dragStartX = 0;
  #dragStartPosition = 0;
  #travelled = 0;
  #resumeTimer = null;

  constructor(element, lane, row, { seconds, direction, pauseOnHover, speedScale }) {
    this.#element = element;
    this.#lane = lane;
    this.#row = row;
    this.#seconds = seconds;
    this.#direction = direction;
    this.#pauseOnHover = pauseOnHover;
    this.#speedScale = speedScale || 1;

    element.addEventListener('pointerenter', this.#handlePointerEnter);
    element.addEventListener('pointerleave', this.#handlePointerLeave);
    element.addEventListener('pointerdown', this.#handlePointerDown);
    element.addEventListener('dragstart', this.#preventDrag);

    // measure() sizes the loop first, then fills to match it.
    this.measure();
  }

  get paused() {
    return this.#dragging || (this.#hovering && this.#pauseOnHover);
  }

  /**
   * Clone the row until the lane is long enough that no offset can expose its end.
   *
   * The offset walks over [0, loopWidth), so the visible window reaches as far as
   * loopWidth + clientWidth into the lane — a reversed row jumps straight to +loopWidth on
   * its very first frame. Filling to twice the viewport was not enough whenever one row was
   * wider than the viewport: the window ran off the end of the content and the row showed
   * blank until it wrapped back around.
   */
  fill() {
    const needed = this.#element.clientWidth + Math.max(this.#loopWidth, 0) + 1;

    for (let i = 0; i < MAX_CLONES; i += 1) {
      if (this.#lane.scrollWidth >= needed) break;

      const clone = /** @type {HTMLElement} */ (this.#row.cloneNode(true));
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('role');
      clone.removeAttribute('data-ih-source');
      // Decorative duplicates must not reach the a11y tree or the theme editor.
      for (const node of clone.querySelectorAll('[id]')) node.removeAttribute('id');
      for (const node of clone.querySelectorAll('[data-shopify-editor-block]')) {
        node.removeAttribute('data-shopify-editor-block');
      }
      this.#lane.append(clone);
    }
  }

  /**
   * Re-measure the loop. Called on resize and once images have loaded, because a row measured
   * before its cards have height is narrower than the real thing — that stale width is what
   * used to leave a lane under-filled.
   */
  measure() {
    const gap = Number.parseFloat(getComputedStyle(this.#lane).columnGap) || 0;
    this.#loopWidth = this.#row.offsetWidth + gap;
    // `speed` is seconds per loop of the FULL card set. Splitting the set across mobile rows
    // shortens each loop, so without this scale the same setting ran the mobile rows at half
    // the desktop rate.
    this.#pixelsPerMs =
      this.#seconds > 0 ? (this.#loopWidth * this.#speedScale) / (this.#seconds * 1000) : 0;
    this.fill();
    this.#apply();
  }

  tick(delta) {
    if (this.paused || this.#loopWidth <= 0) return;

    this.#position += this.#pixelsPerMs * delta * this.#direction;
    this.#apply();
  }

  #apply() {
    if (this.#loopWidth > 0) {
      // Keep the offset inside one loop so the float never grows large enough to lose
      // sub-pixel precision.
      this.#position %= this.#loopWidth;
      if (this.#position < 0) this.#position += this.#loopWidth;
    }

    this.#lane.style.transform = `translate3d(${-this.#position}px, 0, 0)`;
  }

  /* Hover pause is a mouse affordance. iOS raises pointerenter on a tap and can leave the
     element in a sticky hover state with no matching pointerleave, which would park the
     marquee for good on a real device. */
  #handlePointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#hovering = true;
  };

  #handlePointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#hovering = false;
  };

  #preventDrag = (event) => event.preventDefault();

  #handlePointerDown = (event) => {
    if (event.button !== 0 && event.pointerType === 'mouse') return;

    this.#pointerId = event.pointerId;
    this.#dragStartX = event.clientX;
    this.#dragStartPosition = this.#position;
    this.#travelled = 0;
    this.#dragging = true;
    this.#element.setAttribute('data-dragging', '');

    window.addEventListener('pointermove', this.#handlePointerMove);
    window.addEventListener('pointerup', this.#handlePointerUp);
    window.addEventListener('pointercancel', this.#handlePointerUp);
  };

  #handlePointerMove = (event) => {
    if (this.#pointerId === null || event.pointerId !== this.#pointerId) return;

    const dx = event.clientX - this.#dragStartX;
    this.#travelled = Math.max(this.#travelled, Math.abs(dx));
    if (this.#travelled <= DRAG_THRESHOLD) return;

    // `touch-action: pan-y` already gave us the horizontal axis, so there is no scroll to
    // fight here — just move the lane with the finger.
    this.#position = this.#dragStartPosition - dx;
    this.#apply();
  };

  #handlePointerUp = () => {
    if (this.#pointerId === null) return;

    this.#pointerId = null;
    this.#element.removeAttribute('data-dragging');

    window.removeEventListener('pointermove', this.#handlePointerMove);
    window.removeEventListener('pointerup', this.#handlePointerUp);
    window.removeEventListener('pointercancel', this.#handlePointerUp);

    this.#holdAfterInteraction();
  };

  /** Sit still for a moment after a manual move, then drift on from where it was left. */
  #holdAfterInteraction() {
    this.#dragging = true;

    if (this.#resumeTimer) clearTimeout(this.#resumeTimer);
    this.#resumeTimer = setTimeout(() => {
      this.#resumeTimer = null;
      this.#dragging = false;
    }, RESUME_DELAY);
  }

  destroy() {
    const element = this.#element;

    element.removeEventListener('pointerenter', this.#handlePointerEnter);
    element.removeEventListener('pointerleave', this.#handlePointerLeave);
    element.removeEventListener('pointerdown', this.#handlePointerDown);
    element.removeEventListener('dragstart', this.#preventDrag);

    window.removeEventListener('pointermove', this.#handlePointerMove);
    window.removeEventListener('pointerup', this.#handlePointerUp);
    window.removeEventListener('pointercancel', this.#handlePointerUp);

    if (this.#resumeTimer) clearTimeout(this.#resumeTimer);
  }
}

class IngredientHighlightsSlider extends HTMLElement {
  /** Pristine copies of the cards, so a rebuild can always start from the authored order. */
  #cards = [];

  /** @type {MarqueeTrack[]} */
  #tracks = [];

  #rafId = null;
  #lastFrame = 0;
  #offscreen = false;

  /** @type {MediaQueryList | null} */
  #mobileQuery = null;

  /** @type {MediaQueryList | null} */
  #motionQuery = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  connectedCallback() {
    const source = this.querySelector('[data-ih-source]');
    if (!source) return;

    this.#cards = Array.from(source.children, (card) => card.cloneNode(true));
    if (this.#cards.length === 0) return;

    this.#mobileQuery = window.matchMedia(MOBILE_QUERY);
    this.#motionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    this.#mobileQuery.addEventListener('change', this.#build);
    this.#motionQuery.addEventListener('change', this.#build);

    this.#build();

    this.#resizeObserver = new ResizeObserver(this.#measure);
    this.#resizeObserver.observe(this);

    // Cards are lazy-loaded, so the first measure can land before they have their real
    // width. Re-measure once everything is in.
    if (document.readyState !== 'complete') {
      window.addEventListener('load', this.#measure, { once: true });
    }

    // Don't burn frames on a row nobody can see.
    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      this.#offscreen = !entry?.isIntersecting;
    });
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#stop();
    this.#destroyTracks();

    this.#mobileQuery?.removeEventListener('change', this.#build);
    this.#motionQuery?.removeEventListener('change', this.#build);
    this.#resizeObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
    window.removeEventListener('load', this.#measure);

    this.#cards = [];
  }

  get #rowCount() {
    if (!this.#mobileQuery?.matches) return 1;

    const requested = Number.parseInt(this.getAttribute('mobile-rows') || '', 10) || 1;
    // Never ask for more rows than there are cards to fill them.
    return Math.max(1, Math.min(requested, this.#cards.length));
  }

  /** Rebuild the tracks for the current breakpoint. */
  #build = () => {
    this.#stop();
    this.#destroyTracks();
    this.replaceChildren();

    const rowCount = this.#rowCount;
    const perRow = Math.ceil(this.#cards.length / rowCount);

    const seconds = Number.parseFloat(this.getAttribute('speed') || '') || 60;
    const baseDirection = this.getAttribute('direction') === 'reverse' ? -1 : 1;
    const pauseOnHover = this.hasAttribute('pause-on-hover');

    for (let index = 0; index < rowCount; index += 1) {
      const slice = this.#cards.slice(index * perRow, (index + 1) * perRow);
      if (slice.length === 0) continue;

      const track = document.createElement('div');
      track.className = 'ih__track';

      // The transform has to move every copy of the row together, so they share a lane
      // inside the track. The track stays the fixed viewport.
      const lane = document.createElement('div');
      lane.className = 'ih__lane';

      const row = document.createElement('ul');
      row.className = 'ih__row';
      row.setAttribute('role', 'list');
      if (index === 0) row.setAttribute('data-ih-source', '');
      row.append(...slice.map((card) => card.cloneNode(true)));

      lane.append(row);
      track.append(lane);
      this.append(track);

      this.#tracks.push(
        new MarqueeTrack(track, lane, row, {
          seconds,
          // Alternate, so the second mobile row runs against the first.
          direction: index % 2 === 0 ? baseDirection : -baseDirection,
          pauseOnHover,
          speedScale: rowCount,
        })
      );
    }

    // Under reduced motion the rows stay put — still draggable by hand.
    if (!this.#motionQuery?.matches) this.#start();
  };

  #destroyTracks() {
    for (const track of this.#tracks) track.destroy();
    this.#tracks = [];
  }

  #measure = () => {
    for (const track of this.#tracks) track.measure();
  };

  #start() {
    if (this.#rafId !== null) return;
    this.#lastFrame = 0;
    this.#rafId = requestAnimationFrame(this.#tick);
  }

  #stop() {
    if (this.#rafId === null) return;
    cancelAnimationFrame(this.#rafId);
    this.#rafId = null;
  }

  #tick = (now) => {
    this.#rafId = requestAnimationFrame(this.#tick);

    // Skip the first frame so a long delta (tab restore) can't jump the rows.
    if (!this.#lastFrame) {
      this.#lastFrame = now;
      return;
    }

    const delta = Math.min(now - this.#lastFrame, 100);
    this.#lastFrame = now;

    if (this.#offscreen) return;

    for (const track of this.#tracks) track.tick(delta);
  };
}

if (!customElements.get('ingredient-highlights-slider')) {
  customElements.define('ingredient-highlights-slider', IngredientHighlightsSlider);
}
